import { useEffect, useState, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../lib/auth';
import {
  HomeIcon,
  BookOpenIcon,
  FolderIcon,
  TagIcon,
  UsersIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ScaleIcon,
  WrenchScrewdriverIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ShoppingCartIcon,
  Cog6ToothIcon,
  CubeIcon,
  ChatBubbleLeftRightIcon,
  ShareIcon,
  PhotoIcon,
  BanknotesIcon,
  DocumentDuplicateIcon,
  SparklesIcon,
  SwatchIcon,
  StarIcon,
  GlobeAltIcon,
  TicketIcon,
  LifebuoyIcon,
  ShieldCheckIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline';

interface DashboardLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
}

type NavItemId = 'home' | 'metrics' | 'recipes' | 'categories' | 'subcategories' | 'users' | 'units' | 'equipments' | 'chefs' | 'posts' | 'communityRecipes' | 'chefRequests' | 'cuisines' | 'specialties' | 'chefLanguages' | 'chefOnDemand' | 'orders' | 'manualPayments' | 'paymentInstructions' | 'hygieneSafety' | 'promoCodes' | 'inventory' | 'contact' | 'supportRequests' | 'banners' | 'globalNotifications' | 'settings' | 'pantryCategories' | 'pantryIngredients';

interface NavItem {
  id: NavItemId;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
}

const navItems: NavItem[] = [
  { id: 'home', name: 'Dashboard', icon: HomeIcon, path: '/' },
  { id: 'metrics', name: 'KPI Metrics', icon: ChartBarIcon, path: '/' },
  { id: 'recipes', name: 'Recipes', icon: BookOpenIcon, path: '/' },
  { id: 'categories', name: 'Categories', icon: FolderIcon, path: '/' },
  { id: 'subcategories', name: 'Subcategories', icon: TagIcon, path: '/' },
  { id: 'users', name: 'Users', icon: UsersIcon, path: '/' },
  { id: 'orders', name: 'Orders', icon: ShoppingCartIcon, path: '/' },
  { id: 'manualPayments', name: 'Manual Payments', icon: BanknotesIcon, path: '/' },
  { id: 'paymentInstructions', name: 'Payment Instructions', icon: DocumentDuplicateIcon, path: '/' },
  { id: 'hygieneSafety', name: 'Hygiene & Safety', icon: ShieldCheckIcon, path: '/' },
  { id: 'promoCodes', name: 'Promo codes', icon: TicketIcon, path: '/' },
  { id: 'inventory', name: 'Inventory', icon: CubeIcon, path: '/' },
  { id: 'contact', name: 'Contact', icon: ChatBubbleLeftRightIcon, path: '/' },
  { id: 'supportRequests', name: 'Support Requests', icon: LifebuoyIcon, path: '/' },
  { id: 'chefs', name: 'Chefs', icon: UserGroupIcon, path: '/' },
  { id: 'chefRequests', name: 'Chef Requests', icon: ClipboardDocumentCheckIcon, path: '/' },
  { id: 'cuisines', name: 'Cuisines', icon: SwatchIcon, path: '/' },
  { id: 'specialties', name: 'Specialties', icon: StarIcon, path: '/' },
  { id: 'chefLanguages', name: 'Chef languages', icon: GlobeAltIcon, path: '/' },
  { id: 'chefOnDemand', name: 'Chef On Demand', icon: SparklesIcon, path: '/' },
  { id: 'posts', name: 'Posts', icon: DocumentTextIcon, path: '/' },
  { id: 'communityRecipes', name: 'Community Recipes', icon: ShareIcon, path: '/' },
  { id: 'units', name: 'Units', icon: ScaleIcon, path: '/' },
  { id: 'equipments', name: 'Equipments', icon: WrenchScrewdriverIcon, path: '/' },
  { id: 'globalNotifications', name: 'Global notifications', icon: MegaphoneIcon, path: '/' },
  { id: 'banners', name: 'Banners', icon: PhotoIcon, path: '/banners' },
  { id: 'pantryCategories', name: 'Pantry Categories', icon: FolderIcon, path: '/' },
  { id: 'pantryIngredients', name: 'Pantry Ingredients', icon: CubeIcon, path: '/' },
  { id: 'settings', name: 'Settings', icon: Cog6ToothIcon, path: '/' },
];

const navIdToKey: Record<NavItemId, string> = {
  home: 'dashboard',
  metrics: 'kpiMetrics',
  recipes: 'recipes',
  categories: 'categories',
  subcategories: 'subcategories',
  users: 'users',
  units: 'units',
  equipments: 'equipments',
  chefs: 'chefs',
  posts: 'posts',
  communityRecipes: 'communityRecipes',
  chefRequests: 'chefRequests',
  cuisines: 'cuisines',
  specialties: 'specialties',
  chefLanguages: 'chefLanguages',
  chefOnDemand: 'chefOnDemand',
  orders: 'orders',
  manualPayments: 'manualPayments',
  paymentInstructions: 'paymentInstructions',
  hygieneSafety: 'hygieneSafety',
  promoCodes: 'promoCodes',
  inventory: 'inventory',
  contact: 'contact',
  supportRequests: 'supportRequests',
  banners: 'banners',
  globalNotifications: 'globalNotifications',
  pantryCategories: 'pantryCategories',
  pantryIngredients: 'pantryIngredients',
  settings: 'settings',
};

export default function DashboardLayout({ children, title, subtitle }: DashboardLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [user, _setUser] = useState(authService.getCurrentUser());
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const isRtl = i18n.language === 'ar';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    authService.logout();
    window.location.href = '/login';
  };

  // Get user initials for avatar
  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Get active tab from URL query parameter
  const getActiveTab = (): NavItemId => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab && navItems.some(item => item.id === tab as NavItemId)) {
      return tab as NavItemId;
    }
    // Fallback to determine active tab based on path for form pages
    const path = location.pathname;
    if (path.startsWith('/recipes')) return 'recipes';
    if (path.startsWith('/categories')) return 'categories';
    if (path.startsWith('/subcategories')) return 'subcategories';
    if (path.startsWith('/users')) return 'users';
    if (path.startsWith('/orders')) return 'orders';
    if (path.startsWith('/manual-payments')) return 'manualPayments';
    if (path.startsWith('/payment-instructions')) return 'paymentInstructions';
    if (path.startsWith('/hygiene-safety')) return 'hygieneSafety';
    if (path.startsWith('/inventory')) return 'inventory';
    if (path.startsWith('/contact')) return 'contact';
    if (path.startsWith('/support-requests')) return 'supportRequests';
    if (path.startsWith('/chefs')) return 'chefs';
    if (path.startsWith('/chef-requests')) return 'chefRequests';
    if (path.startsWith('/posts')) return 'posts';
    if (path.startsWith('/community-recipes')) return 'communityRecipes';
    if (path.startsWith('/units')) return 'units';
    if (path.startsWith('/equipment')) return 'equipments';
    if (path.startsWith('/banners')) return 'banners';
    if (path.startsWith('/pantry-categories')) return 'pantryCategories';
    if (path.startsWith('/pantry-ingredients')) return 'pantryIngredients';
    return 'home';
  };

  const activeTab = getActiveTab();

  const sortedNavItems = useMemo(() => {
    const locale = i18n.resolvedLanguage || i18n.language;
    return [...navItems].sort((a, b) => {
      const labelA = t(`nav.${navIdToKey[a.id]}`);
      const labelB = t(`nav.${navIdToKey[b.id]}`);
      return labelA.localeCompare(labelB, locale, { sensitivity: 'base' });
    });
  }, [t, i18n.language, i18n.resolvedLanguage]);

  const handleNavClick = (item: NavItem) => {
    // Use dedicated path for banners; others use tab on home
    if (item.path !== '/') {
      navigate(item.path);
    } else {
      navigate(`/?tab=${item.id}`);
    }
  };

  const displayTitle = title || t(`nav.${navIdToKey[activeTab]}`);
  const displaySubtitle = subtitle || t('layout.subtitle');

  return (
    <div dir={isRtl ? 'rtl' : 'ltr'} className={`min-h-screen bg-gray-50 flex flex-row ${isRtl ? 'flex-row-reverse' : ''}`}>
      <aside
        className={`${sidebarOpen ? 'w-72' : 'w-20'} bg-black text-white transition-all duration-300 ease-in-out flex flex-col fixed h-screen z-40 ${isRtl ? 'right-0 left-auto' : 'left-0 right-auto'}`}
      >
        <div className={`flex items-center justify-between p-4 border-b border-gray-800 ${isRtl ? 'flex-row-reverse' : ''}`}>
          <div className={`flex items-center ${sidebarOpen ? 'opacity-100' : 'opacity-0 w-0'} ${isRtl ? 'flex-row-reverse gap-2' : 'space-x-3'}`}>
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center text-2xl shadow-lg flex-shrink-0">
              🍳
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-white truncate">Cravela</h1>
              <p className="text-xs text-gray-400 truncate">{t('nav.adminPanel')}</p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-800 transition-colors flex-shrink-0"
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? (
              <XMarkIcon className="w-5 h-5" />
            ) : (
              <Bars3Icon className="w-5 h-5" />
            )}
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          <div className={`px-3 space-y-1 ${isRtl ? 'text-right' : ''}`}>
            {sortedNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  className={`w-full flex items-center px-4 py-3 rounded-lg transition-all duration-200 ${isRtl ? 'flex-row-reverse justify-end gap-3' : 'space-x-3'} ${
                    isActive
                      ? 'bg-primary-orange text-white shadow-lg'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <Icon className="w-6 h-6 flex-shrink-0" />
                  {sidebarOpen && (
                    <span className="font-medium truncate">{t(`nav.${navIdToKey[item.id]}`)}</span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* User Profile Section */}
        <div className="border-t border-gray-800 p-4">
          <div className="relative" ref={profileMenuRef}>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className={`w-full flex items-center px-4 py-3 rounded-lg hover:bg-gray-800 transition-colors ${
                sidebarOpen ? (isRtl ? 'justify-end flex-row-reverse' : 'justify-start space-x-3') : 'justify-center'
              } ${!sidebarOpen && isRtl ? 'flex-row-reverse' : ''}`}
            >
              <div className="w-10 h-10 bg-gradient-to-br from-primary-orange to-orange-600 rounded-full flex items-center justify-center text-white font-semibold shadow-lg flex-shrink-0">
                {user?.name ? getUserInitials(user.name) : 'A'}
              </div>
              {sidebarOpen && (
                <div className={`min-w-0 flex-1 ${isRtl ? 'text-right' : 'text-left'}`}>
                  <p className="text-sm font-medium text-white truncate">
                    {user?.name || t('nav.adminUser')}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {user?.email || 'admin@cravela.com'}
                  </p>
                </div>
              )}
            </button>

            {showProfileMenu && (
              <div className={`absolute bottom-full mb-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50 ${isRtl ? 'right-4 left-auto' : 'left-4'}`}>
                <div className="px-4 py-3 border-b border-gray-200">
                  <p className="text-sm font-semibold text-gray-900">{user?.name || t('nav.adminUser')}</p>
                  <p className="text-sm text-gray-500 truncate">{user?.email || 'admin@cravela.com'}</p>
                </div>
                <div className="py-1">
                  <button
                    onClick={handleLogout}
                    className={`w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors ${isRtl ? 'flex-row-reverse justify-end gap-3' : 'space-x-3'}`}
                  >
                    <ArrowRightOnRectangleIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    <span>{t('nav.logout')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? (isRtl ? 'mr-72 ml-0' : 'ml-72') : isRtl ? 'mr-20 ml-0' : 'ml-20'}`}>
        <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
          <div className={`px-6 py-4 ${isRtl ? 'text-right' : ''}`}>
            <div className={`flex items-center justify-between ${isRtl ? 'flex-row-reverse' : ''}`}>
              <div className={isRtl ? 'text-right' : ''}>
                <h2 className="text-2xl font-bold text-gray-900">{displayTitle}</h2>
                <p className="text-sm text-gray-500 mt-1">{displaySubtitle}</p>
              </div>
              <div className={`flex items-center gap-2 flex-shrink-0 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <span className="text-sm font-medium text-gray-500">{t('nav.language')}:</span>
                <button
                  type="button"
                  onClick={() => i18n.changeLanguage('en')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    i18n.language === 'en' ? 'bg-orange-500 text-white shadow' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  EN
                </button>
                <button
                  type="button"
                  onClick={() => i18n.changeLanguage('ar')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    i18n.language === 'ar' ? 'bg-orange-500 text-white shadow' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  العربية
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="px-6 pt-8 pb-6 admin-dashboard">
          {children}
        </main>
      </div>
    </div>
  );
}

